import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback
} from 'react';
import { authService } from '@/services/authService';
import { getErrorMessage } from '@/api/axios';
import type { User, AuthState, UserInfo } from '@/types';

const FULL_ADMIN_ROLE = 'Admin-Appy-Full';

const getSafeRedirectPath = (url: string | null): string | null => {
  if (!url || typeof url !== 'string') {
    return null;
  }
  if (url.length > 2048) {
    return null;
  }
  if (/[\x00-\x1F\x7F]/.test(url) || url.trim() !== url) {
    return null;
  }
  if (!url.startsWith('/') || url.startsWith('//') || url.startsWith('/\\')) {
    return null;
  }
  try {
    const resolved = new URL(url, window.location.origin);
    if (resolved.origin !== window.location.origin) {
      return null;
    }
    return resolved.pathname + resolved.search + resolved.hash;
  } catch {
    return null;
  }
};

interface AuthContextType extends AuthState {
  login: (username: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => Promise<void>;
  updateProfile: (updates: Partial<User>) => Promise<{ success: boolean; error?: string }>;
  clearError: () => void;
  hasPermission: (permission: string) => boolean;
  hasRole: (role: string) => boolean;
  refreshProfile: () => Promise<void>;
  isFullAdmin: boolean;
}

interface AuthProviderProps {
  children: ReactNode;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const mapUserInfoToUser = (userInfo: UserInfo): User => {
  return {
    id: userInfo.id,
    email: userInfo.email,
    username: userInfo.username,
    name: userInfo.fullName || `${userInfo.firstName} ${userInfo.lastName}`,
    firstName: userInfo.firstName,
    lastName: userInfo.lastName,
    role: userInfo.userRole,
    userRole: userInfo.userRole,
    avatar: null,
    phone: userInfo.phone,
    idAco: userInfo.idAco,
    permissions: userInfo.permissions,
    groups: userInfo.groups,
  };
};

export function AuthProvider({ children }: AuthProviderProps): JSX.Element {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkAuth = async (): Promise<void> => {
      try {
        const response = await authService.checkSession();

        if (response.data?.authenticated && response.data?.id) {
          const profileResponse = await authService.getProfile();

          if (profileResponse.data) {
            setUser(mapUserInfoToUser(profileResponse.data));
          }
        }
      } catch (err) {
        console.log('No active session');
      } finally {
        setIsLoading(false);
      }
    };

    void checkAuth();
  }, []);

  const login = useCallback(async (
    username: string,
    password: string
  ): Promise<{ success: boolean; error?: string }> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authService.login(username, password);

      if (response.data) {
        setUser(mapUserInfoToUser(response.data));
        const storedRedirect = sessionStorage.getItem('redirectAfterLogin');
        sessionStorage.removeItem('redirectAfterLogin');

        const safePath = getSafeRedirectPath(storedRedirect);
        if (safePath) {
          window.location.href = safePath;
        }

        return { success: true };
      } else {
        const errorMsg = response.message || 'Error al iniciar sesión';
        setError(errorMsg);
        return { success: false, error: errorMsg };
      }
    } catch (err: unknown) {
      const errorMsg = getErrorMessage(err);
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setIsLoading(false);
    }
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    setIsLoading(true);

    try {
      await authService.logout();
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      setUser(null);
      setIsLoading(false);
    }
  }, []);

  const updateProfile = useCallback(async (
    updates: Partial<User>
  ): Promise<{ success: boolean; error?: string }> => {
    setIsLoading(true);

    try {
      await authService.updateProfile({
        firstName: updates.firstName || user?.firstName || '',
        lastName: updates.lastName || user?.lastName || '',
        userRole: (updates.userRole || user?.userRole || 'operator') as 'operator' | 'comissionist',
        phone: updates.phone || undefined,
      });

      const profileResponse = await authService.getProfile();
      if (profileResponse.data) {
        setUser(mapUserInfoToUser(profileResponse.data));
      }

      return { success: true };
    } catch (err: unknown) {
      const errorMsg = getErrorMessage(err);
      return { success: false, error: errorMsg };
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  const refreshProfile = useCallback(async (): Promise<void> => {
    try {
      const profileResponse = await authService.getProfile();
      if (profileResponse.data) {
        setUser(mapUserInfoToUser(profileResponse.data));
      }
    } catch (err) {
      console.error('Error refreshing profile:', err);
    }
  }, []);

  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  const hasPermission = useCallback((permission: string): boolean => {
    if (!user?.permissions) return false;
    return user.permissions.includes(permission) || user.permissions.includes('all');
  }, [user]);

  const hasRole = useCallback((role: string): boolean => {
    if (!user) return false;
    return user.role === role || user.permissions?.includes(role) || false;
  }, [user]);

  const isFullAdmin = !!user?.permissions?.includes(FULL_ADMIN_ROLE);

  const value: AuthContextType = {
    user,
    isLoading,
    error,
    isAuthenticated: !!user,
    login,
    logout,
    updateProfile,
    clearError,
    hasPermission,
    hasRole,
    refreshProfile,
    isFullAdmin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;